/**
 * The composer takeover: the agent's questions, in the slot the text box was in.
 *
 * This is `PlanningChat/ui/Question.tsx` ported — one question per screen, `N of M`, Skip / Next /
 * Send, a summary with editable rows once there is more than one question, and the three input
 * shapes (`open` a textarea, `single` numbered radios plus a freetext, `multiple` checkboxes plus a
 * freetext) — with two things changed on purpose.
 *
 * **1. It is controlled.** The old card took four `initial*` props to hydrate internal state, two of
 * them `Map`s. API-PARITY §1.7: a `Map` prop is not serialisable, breaks referential equality every
 * render, and leaves the parent unable to re-render the card from server state. Here the answers and
 * the screen are props, every change is an `onChange`, and the component holds exactly one piece of
 * state of its own — which textarea has the caret in it, which is DOM focus and nothing the server
 * could ever know.
 *
 * **2. Nothing is imported from `PlanningChat/ui`.** The exits are `PillButton` in a
 * `PillActionRow`; the shell is `Composer`'s own box, restated in this folder's stylesheet because
 * the composer's card is a component we may not reach inside. The two `Button`s and the two
 * `DeckIcon`s the old card used are replaced by v1.5's `PillButton` and `ActivityGlyph`, so the card
 * reads in `--cv15-*` and follows the theme like everything else in the pane.
 *
 * ## What is NOT `AnswerChip`, and why
 *
 * The chip is v1.5's part for recording a choice and the inline route uses it (see
 * `QuestionBlock.tsx`). It cannot do this card: a chip renders a label and nothing else, and every
 * option row here leads with a number or a checkbox. Those leads are the behaviour §1.7 says to
 * keep, so the rows stay full-width rows. The chip and the row are answering different questions —
 * one is "pick one of three, now", the other is "work through five of these".
 */
import { useCallback, useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import {
  ActivityGlyph,
  PillActionRow,
  PillButton,
  chatThemeClass,
  type ChatTheme,
} from '~/design-system/components/v1_5/Chat';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import { cn } from '~/lib/utils';
import styles from './QuestionCard.module.css';
import {
  answerText,
  clearForFreeText,
  isAnswered,
  pickOption,
  selectedCount,
  setFreeText,
  splitValues,
  type QuestionAnswers,
  type QuestionData,
} from './questionModel';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * Spread rather than written as an attribute: React 18's DOM typings predate `inert`, and `inert=""`
 * is the attribute's own present form in the HTML. Same trick, and the same reason, as `Composer`.
 */
const INERT = { inert: '' };

/** The card's own scroll ceiling for a long list of options, so five never push the pane off. */
const CONTENT: CSSProperties = { maxHeight: 260, overflowY: 'auto', scrollbarWidth: 'none' };

export interface QuestionCardProps {
  /** One to five, in the order the agent asked them. */
  questions: QuestionData[];
  /** The controlled answer map. Values are labels; see `questionModel.ts`. */
  answers: QuestionAnswers;
  /**
   * Which screen is showing. `0 … questions.length - 1` is a question; **`questions.length` is the
   * summary**, which is only reachable when there is more than one question.
   *
   * One number rather than an index plus a `showSummary` flag, because two pieces of state that can
   * disagree about which screen is up is exactly the bug a controlled component exists to prevent.
   */
  currentIndex: number;
  onChange: (answers: QuestionAnswers) => void;
  onIndexChange: (index: number) => void;
  /** Send what is on the card. Unanswered questions go up as `skipped`. */
  onSubmit: () => void;
  /** Refuse the whole request — the x, and Skip on the last screen with nothing filled in. */
  onSkipAll: () => void;
  /** The answer is in flight. The card dims and goes `inert` rather than half-responding. */
  submitting?: boolean;
  theme?: ChatTheme;
  className?: string;
}

export function QuestionCard({
  questions,
  answers,
  currentIndex,
  onChange,
  onIndexChange,
  onSubmit,
  onSkipAll,
  submitting = false,
  theme: themeProp,
  className,
}: QuestionCardProps) {
  const theme = useV15Theme(themeProp);
  const total = questions.length;
  const stepper = total > 1;
  const summaryIndex = total;
  // Clamped rather than trusted: the request can be re-seeded under a card that had walked past the
  // end of the new question list, and an out-of-range index would read `undefined.question`.
  const index = Math.min(Math.max(currentIndex, 0), summaryIndex);
  const onSummary = stepper && index === summaryIndex;

  const question = questions[Math.min(index, total - 1)];
  const values = answers[question?.id ?? ''] ?? [];
  const { optionIds, text } = splitValues(question ?? { id: '', question: '', type: 'open' }, values);

  const areaRef = useRef<HTMLTextAreaElement>(null);

  /** The one thing this card keeps to itself: the box grows with what is in it. */
  const grow = useCallback((element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = 'auto';
    element.style.height = `${Math.min(element.scrollHeight, 120)}px`;
  }, []);

  useEffect(() => grow(areaRef.current), [grow, text, index]);

  const write = useCallback(
    (next: string[]) => {
      if (!question) return;
      onChange({ ...answers, [question.id]: next });
    },
    [answers, onChange, question],
  );

  /* ── moving between screens ───────────────────────────────────────────────────────────────── */

  const last = index === total - 1;
  const anyAnswer = questions.some((item) => isAnswered(answers[item.id]));

  const next = useCallback(() => {
    if (submitting) return;
    if (!last) {
      onIndexChange(index + 1);
      return;
    }
    if (stepper) onIndexChange(summaryIndex);
    else onSubmit();
  }, [index, last, onIndexChange, onSubmit, stepper, submitting, summaryIndex]);

  /**
   * Skip is Next with one difference, and it is the one the old card drew: on the LAST question,
   * Next always goes to the summary while Skip only does so if something, anywhere, has been
   * answered. Skipping the last of five having answered none of them sends the refusal directly
   * rather than showing a summary of nothing.
   */
  const skip = useCallback(() => {
    if (submitting) return;
    if (!last) {
      onIndexChange(index + 1);
      return;
    }
    if (stepper && anyAnswer) onIndexChange(summaryIndex);
    else onSubmit();
  }, [anyAnswer, index, last, onIndexChange, onSubmit, stepper, submitting, summaryIndex]);

  /**
   * BACKWARDS, which this card simply could not do.
   *
   * Every route through it was one-way: Next, Skip and Enter all advance, the summary is only
   * reachable from the last screen, and a question you skipped was gone — there was no way to
   * reconsider it short of refusing the whole request and asking the agent again (Beka,
   * 2026-09-20). The summary's editable rows were the only way back and they only list questions
   * that were ANSWERED, so the one screen you would want to return to is the one it cannot offer.
   *
   * Forward is `next` without the send: on the last question it lands on the summary exactly as
   * Next does, and on a card with no summary there is nothing past the end, so the pager is not
   * drawn at all.
   */
  const back = useCallback(() => {
    if (submitting || index === 0) return;
    onIndexChange(index - 1);
  }, [index, onIndexChange, submitting]);

  const forward = useCallback(() => {
    if (submitting || index >= summaryIndex) return;
    onIndexChange(index + 1);
  }, [index, onIndexChange, submitting, summaryIndex]);

  const onFreeTextKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key !== 'Enter' || event.shiftKey) return;
      // The composition guard the composer carries, for the same reason: during a Japanese or
      // Korean composition Enter commits the candidate and must not also advance the card.
      if (event.nativeEvent.isComposing) return;
      event.preventDefault();
      // The old card swallowed this key and did nothing with it, which left the only way forward
      // out of a typed answer as a trip to the mouse. Enter advances, exactly like the composer.
      next();
    },
    [next],
  );

  if (total === 0) return null;

  /* ── the summary ──────────────────────────────────────────────────────────────────────────── */

  const shell = cn(chatThemeClass(theme), styles.card, submitting && styles.busy, className);
  const guard = submitting ? INERT : null;

  if (onSummary) {
    return (
      <div className={shell} {...guard}>
        <div className={styles.header}>
          <p className={styles.heading}>Summary</p>
          <button type="button" className={styles.close} onClick={onSkipAll} aria-label="Skip these questions">
            <ActivityGlyph chrome="close" size={14} tone="inherit" theme={theme} />
          </button>
        </div>

        <div className={styles.content} style={CONTENT}>
          {questions.map((item, itemIndex) => {
            const written = answerText(answers[item.id]);
            // Unanswered rows are left off rather than listed as blanks: the summary is a receipt
            // of what will be sent, and a question with nothing on it is going up as skipped.
            if (!written) return null;
            return (
              <button
                key={item.id}
                type="button"
                className={styles.summaryItem}
                onClick={() => onIndexChange(itemIndex)}
              >
                <span className={styles.summaryHead}>
                  <span className={styles.summaryQuestion}>{item.question}</span>
                  <span className={styles.editMark}>
                    <DeckIcon name="edit" size={16} />
                  </span>
                </span>
                <span className={styles.summaryAnswer}>{written}</span>
              </button>
            );
          })}
        </div>

        {/* The summary's own way back, and it matters most on the summary that has nothing on it:
            the editable rows below only list questions that were ANSWERED, so arriving here having
            skipped everything used to leave Send and Skip as the only two doors. No forward — the
            summary IS the end, and Send is what is past it. */}
        <Exits
          pager={{ label: `${selectedCount(answers)} selected`, onBack: back }}
          primary="Send"
          onPrimary={submitting ? undefined : onSubmit}
          onGhost={onSkipAll}
          theme={theme}
        />
      </div>
    );
  }

  /* ── one question ─────────────────────────────────────────────────────────────────────────── */

  const open = question.type === 'open';
  const multi = question.type === 'multiple';
  const chosen = new Set(optionIds);
  // On `multiple` the freetext is a checkbox of its own, ticked by there being something in it.
  const freeTicked = multi && text.trim().length > 0;

  return (
    <div className={shell} {...guard}>
      <div className={styles.header}>
        <p className={styles.heading}>{question.question}</p>
        <button type="button" className={styles.close} onClick={onSkipAll} aria-label="Skip these questions">
          <ActivityGlyph chrome="close" size={14} tone="inherit" theme={theme} />
        </button>
      </div>

      {/* A real group, named by the question. Without it a screen reader meets four loose controls
          with no idea which sentence they answer — and `role="radio"` outside a `radiogroup` is not
          a radio at all, it is a button telling a lie about itself. */}
      <div
        className={styles.content}
        style={CONTENT}
        role={open ? undefined : multi ? 'group' : 'radiogroup'}
        aria-label={open ? undefined : question.question}
      >
        {open ? (
          <div className={styles.freeRow}>
            <span className={cn(styles.lead, styles.leadRing)}>
              <DeckIcon name="edit" size={16} />
            </span>
            <textarea
              ref={areaRef}
              className={styles.input}
              placeholder="Type your answer..."
              value={text}
              rows={1}
              aria-label={question.question}
              onChange={(event) => write(setFreeText(question, values, event.target.value))}
              onKeyDown={onFreeTextKeyDown}
            />
          </div>
        ) : (
          <>
            {question.options?.map((option, optionIndex) => {
              const on = chosen.has(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  className={cn(styles.option, on && styles.optionOn)}
                  // The row IS the control, so it carries the state a radio or a checkbox would.
                  // `aria-checked` over `aria-pressed`: these are answers to one question, not a
                  // set of independent toggles.
                  role={multi ? 'checkbox' : 'radio'}
                  aria-checked={on}
                  onClick={() => write(pickOption(question, values, option.id))}
                >
                  <span className={cn(styles.lead, !multi && styles.leadRing)}>
                    {multi ? (
                      <span className={cn(styles.box, on && styles.boxOn)}>
                        {on ? <ActivityGlyph chrome="check" size={14} tone="inherit" theme={theme} /> : null}
                      </span>
                    ) : (
                      <span className={styles.number}>{optionIndex + 1}</span>
                    )}
                  </span>
                  <span className={cn(styles.optionLabel, on && styles.optionLabelOn)}>{option.label}</span>
                </button>
              );
            })}

            <div className={cn(styles.freeRow, text.trim() && styles.freeActive)}>
              <span className={cn(styles.lead, !multi && styles.leadRing)}>
                {multi ? (
                  <span className={cn(styles.box, freeTicked && styles.boxOn)}>
                    {freeTicked ? <ActivityGlyph chrome="check" size={14} tone="inherit" theme={theme} /> : null}
                  </span>
                ) : (
                  <DeckIcon name="edit" size={16} />
                )}
              </span>
              <textarea
                ref={areaRef}
                className={styles.input}
                placeholder="Something else"
                value={text}
                rows={1}
                aria-label={`Something else: ${question.question}`}
                // On a `single` question, reaching for the box gives up the radio — the old card
                // did the same, and it is what keeps one answer meaning one answer.
                onFocus={() => write(clearForFreeText(question, values))}
                onChange={(event) => write(setFreeText(question, values, event.target.value))}
                onKeyDown={onFreeTextKeyDown}
              />
            </div>
          </>
        )}
      </div>

      {/* The pager REPLACES the `n selected` line that used to sit here, rather than crowding in
          beside it. What is selected is already drawn on the rows immediately above — every one of
          them is ticked or numbered — and the corner the count was holding is the one place the
          position and the way to change it can sit together. The summary keeps the count, having no
          rows to read one off. */}
      <Exits
        pager={
          stepper
            ? {
                label: `${index + 1} of ${total}`,
                // Undefined rather than a no-op on the first screen: the button reads its own
                // disabled state off the absence of somewhere to go.
                onBack: index > 0 ? back : undefined,
                onForward: forward,
              }
            : undefined
        }
        primary={stepper ? 'Next' : 'Send'}
        onPrimary={next}
        onGhost={skip}
        theme={theme}
      />
    </div>
  );
}

/**
 * The pair of exits, identical on every screen — only the strings change.
 *
 * Primary before ghost, which is `PillActionRow`'s own order and `ActivityBlock`'s (Accept before
 * Reject, Try again before Skip it). The old card drew Skip on the left; matching the rest of the
 * agent column matters more than matching the card being replaced, and both pills say what they do.
 *
 * Neither is ever disabled. Pressing Next on a question with nothing on it does precisely what Skip
 * does — the answer goes up marked `skipped` either way — so a greyed-out primary would be
 * describing a difference that does not exist.
 */
function Exits({
  pager,
  primary,
  onPrimary,
  onGhost,
  theme,
}: {
  /**
   * The left corner: back, where you are, forward. Absent on a card with a single question, which
   * has nowhere to go and nothing to count.
   *
   * `onBack` and `onForward` are each the button's own disabled state — a missing handler IS "there
   * is nothing that way", so the two facts cannot drift apart. Back stays drawn while inert so the
   * label beside it does not slide left on the screen you open on; forward is not drawn at all on
   * the summary, because the summary is the end rather than a place with a greyed-out door.
   */
  pager?: { label: string; onBack?: () => void; onForward?: () => void };
  primary: string;
  onPrimary?: () => void;
  onGhost: () => void;
  theme: ChatTheme;
}) {
  return (
    <div className={styles.actions}>
      {pager ? (
        <div className={styles.pager}>
          <button
            type="button"
            className={styles.iconButton}
            onClick={pager.onBack}
            disabled={!pager.onBack}
            aria-label="Previous question"
          >
            <ActivityGlyph chrome="chevron-left" size={14} tone="inherit" theme={theme} />
          </button>
          <span className={styles.step}>{pager.label}</span>
          {pager.onForward ? (
            <button
              type="button"
              className={styles.iconButton}
              onClick={pager.onForward}
              aria-label="Next question"
            >
              <ActivityGlyph chrome="chevron-right" size={14} tone="inherit" theme={theme} />
            </button>
          ) : null}
        </div>
      ) : null}
      <PillActionRow layout="inline" theme={theme} className={styles.exits}>
        <PillButton label={primary} variant="primary" onClick={onPrimary} theme={theme} />
        <PillButton label="Skip" variant="ghost" onClick={onGhost} theme={theme} />
      </PillActionRow>
    </div>
  );
}
